import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Stat } from "@/components/common";
import { fmtMoney, fmtNum } from "@/lib/api";
import { RangePicker, scopedSnapshot } from "@/lib/scope";

const metricCols = (cur) => [
  { key: "cost", label: "Cost", fmt: (v) => fmtMoney(v, cur) },
  { key: "clicks", label: "Clicks", fmt: fmtNum },
  { key: "impressions", label: "Impr.", fmt: fmtNum },
  { key: "conversions", label: "Conv.", fmt: fmtNum },
  { key: "cost_per_conversion", label: "Cost/conv.", fmt: (v) => fmtMoney(v, cur) },
  { key: "ctr", label: "CTR", fmt: (v) => `${fmtNum(v)}%` },
];

const MAX_RENDER = 500;

const DataTable = ({ rows, cols, testid }) =>
  rows.length === 0 ? (
    <p data-testid={`${testid}-empty`} className="py-6 text-sm text-slate-500">No rows in this range yet.</p>
  ) : (
    <div className="max-h-[480px] overflow-auto rounded-md border border-[#1e293b]" data-testid={testid}>
      {rows.length > MAX_RENDER && <div className="border-b border-[#1e293b] px-3 py-2 font-mono text-[11px] text-slate-500">Showing the top {MAX_RENDER} of {rows.length} rows by cost. The audit uses all rows.</div>}
      <table className="w-full text-left text-sm">
        <thead className="sticky top-0 bg-[#0a101d]">
          <tr>{cols.map((c) => <th key={c.key} className="whitespace-nowrap px-3 py-2.5 font-mono text-[10px] font-medium uppercase tracking-wider text-slate-500">{c.label}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-[#1e293b]">
          {rows.slice(0, MAX_RENDER).map((r, i) => (
            <tr key={i} className="transition-colors hover:bg-[#162036]">
              {cols.map((c) => <td key={c.key} className="whitespace-nowrap px-3 py-2 font-mono text-xs text-slate-300">{c.fmt ? c.fmt(r[c.key]) : String(r[c.key] ?? "—")}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

export default function AdsData({ snapshot, range = "launch", setRange }) {
  const cur = snapshot.account?.currency;
  const m = metricCols(cur);
  const v = scopedSnapshot(snapshot, range);
  const t = v.totals;
  const tabs = [
    { id: "campaigns", label: "Campaigns", rows: v.campaigns, cols: [{ key: "name", label: "Campaign" }, { key: "status", label: "Status" }, { key: "bidding_strategy", label: "Bidding" }, { key: "daily_budget", label: "Daily budget", fmt: (x) => fmtMoney(x, cur) }, ...m] },
    { id: "ad-groups", label: "Ad groups", rows: v.ad_groups, cols: [{ key: "name", label: "Ad group" }, { key: "campaign", label: "Campaign" }, { key: "status", label: "Status" }, ...m] },
    { id: "keywords", label: "Keywords", rows: v.keywords, cols: [{ key: "text", label: "Keyword" }, { key: "match_type", label: "Match" }, { key: "quality_score", label: "QS" }, { key: "ad_group", label: "Ad group" }, ...m] },
    { id: "search-terms", label: "Search terms", rows: v.search_terms, cols: [{ key: "term", label: "Search term" }, { key: "status", label: "Status" }, { key: "ad_group", label: "Ad group" }, ...m] },
    { id: "locations", label: "Locations", rows: v.locations, cols: [{ key: "campaign", label: "Campaign" }, { key: "location", label: "Location" }, { key: "negative", label: "Excluded", fmt: (x) => (x ? "Yes" : "No") }] },
    { id: "negatives", label: "Negatives", rows: snapshot.negatives || [], cols: [{ key: "text", label: "Negative keyword" }, { key: "match_type", label: "Match" }, { key: "level", label: "Level" }, { key: "scope", label: "Campaign / list" }] },
  ];
  const s = v.scope;
  return (
    <div className="space-y-6">
      {setRange && <RangePicker range={range} setRange={setRange} testid="ads-range" />}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <Stat label={`${s} · Cost`} value={fmtMoney(t.cost, cur)} testid="kpi-cost" />
        <Stat label={`${s} · Clicks`} value={fmtNum(t.clicks)} testid="kpi-clicks" />
        <Stat label={`${s} · Impressions`} value={fmtNum(t.impressions)} testid="kpi-impressions" />
        <Stat label={`${s} · Conversions`} value={fmtNum(t.conversions)} testid="kpi-conversions" />
        <Stat label={`${s} · Cost / conv.`} value={fmtMoney(t.cost_per_conversion, cur)} testid="kpi-cpa" />
        <Stat label={`${s} · CTR`} value={`${fmtNum(t.ctr)}%`} testid="kpi-ctr" />
      </div>
      {range !== "30d" && <p className="text-[11px] text-slate-500" data-testid="ads-scope-note">Showing only the 3 TRL campaigns. Paused old campaigns are under “Last 30 days (whole account)”. Stored history is unchanged; the audit and optimiser still use all of it.{range === "7d" && " Ad groups are summed from their keywords."}</p>}
      <Tabs defaultValue="campaigns">
        <TabsList className="bg-[#0a101d]">
          {tabs.map((tb) => <TabsTrigger key={tb.id} value={tb.id} data-testid={`ads-tab-${tb.id}`}>{tb.label} <span className="ml-1.5 font-mono text-[10px] text-slate-500">{tb.rows.length}</span></TabsTrigger>)}
        </TabsList>
        {tabs.map((tb) => <TabsContent key={tb.id} value={tb.id} className="mt-4"><DataTable rows={tb.rows} cols={tb.cols} testid={`ads-table-${tb.id}`} /></TabsContent>)}
      </Tabs>
    </div>
  );
}
